import BackButton from "@dashboard/components/BackButton";
import { ConfirmButton, ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import Form from "@dashboard/components/Form";
import Grid from "@dashboard/components/Grid";
import { DashboardModal } from "@dashboard/components/Modal";
import { Select } from "@dashboard/components/Select";
import { ChangeEvent } from "@dashboard/hooks/useForm";
import { commonMessages } from "@dashboard/intl";
import {
  getCitiesByCountryArea,
  getCountriesAreas,
  getPostalCodeByArea,
} from "@dashboard/shipping/countryAreaToPostalCode";
import { DialogProps, MinMax } from "@dashboard/types";
import { TextField } from "@material-ui/core";
import { Text } from "@saleor/macaw-ui-next";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

export interface ShippingZonePostalCodeRangeDialogProps extends DialogProps {
  confirmButtonState: ConfirmButtonTransitionState;
  onSubmit: (range: MinMax) => void;
}

const ShippingZonePostalCodeRangeDialog: React.FC<ShippingZonePostalCodeRangeDialogProps> = ({
  confirmButtonState,
  open,
  onClose,
  onSubmit,
}) => {
  const intl = useIntl();
  const initial: MinMax = {
    max: "",
    min: "",
  };

  const [selectedCountryArea, setSelectedCountryArea] = React.useState("");
  const [selectedCity, setSelectedCity] = React.useState("");
  const [cities, setCities] = React.useState<string[]>([]);

  const handleCountryAreaChange = (event: ChangeEvent<any>, change: any) => {
    const value = event.target.value;

    setSelectedCountryArea(value);
    setCities(getCitiesByCountryArea(value) || []);

    change({
      target: {
        name: "max" as keyof FormData,
        value: "",
      },
    } as any);
    change({
      target: {
        name: "min" as keyof FormData,
        value: "",
      },
    } as any);
  };

  const handleCityChange = (event: ChangeEvent<any>, change: any) => {
    const value = event.target.value;

    setSelectedCity(value);

    const postalCode = getPostalCodeByArea(value) ?? "";

    change({
      target: {
        name: "max" as keyof FormData,
        value: postalCode,
      },
    } as any);
    change({
      target: {
        name: "min" as keyof FormData,
        value: postalCode,
      },
    } as any);
  };

  return (
    <DashboardModal onChange={onClose} open={open}>
      <DashboardModal.Content size="sm">
        <Form initial={initial} onSubmit={onSubmit}>
          {({ change, data }) => (
            <DashboardModal.Grid>
              <DashboardModal.Header>
                <FormattedMessage
                  id="eXPgWN"
                  defaultMessage="Add city"
                  description="dialog header"
                />
              </DashboardModal.Header>

              <Text>
                <FormattedMessage
                  id="Etxy8K"
                  defaultMessage="Please provide the city you would like to add to the include/exclude list."
                />
              </Text>

              <Grid variant="uniform">
                <Select
                  options={getCountriesAreas().map(countryArea => ({
                    label: countryArea,
                    value: countryArea,
                  }))}
                  label={intl.formatMessage({
                    id: "UFpdbZ",
                    defaultMessage: "Region",
                    description: "range input label",
                  })}
                  value={selectedCountryArea}
                  onChange={event => handleCountryAreaChange(event, change)}
                />
                <Select
                  options={cities.map(city => ({
                    label: city,
                    value: city,
                  }))}
                  label={intl.formatMessage({
                    id: "CYKRB7",
                    defaultMessage: "City",
                    description: "range input label",
                  })}
                  value={selectedCity}
                  onChange={event => handleCityChange(event, change)}
                />
                <TextField
                  data-test-id="zip-code-starts-with-input"
                  label={intl.formatMessage({
                    id: "1T1fP8",
                    defaultMessage: "Postal codes (start)",
                    description: "range input label",
                  })}
                  disabled
                  name="min"
                  value={data.min}
                  onChange={change}
                  style={{ visibility: "hidden", height: 0 }}
                />
                <TextField
                  data-test-id="zip-code-ends-with-input"
                  label={intl.formatMessage({
                    id: "axFFaD",
                    defaultMessage: "Postal codes (end)",
                    description: "range input label",
                  })}
                  name="max"
                  disabled
                  helperText={intl.formatMessage(commonMessages.optionalField)}
                  value={data.max}
                  onChange={change}
                  style={{ visibility: "hidden", height: 0 }}
                />
              </Grid>

              <DashboardModal.Actions>
                <BackButton onClick={onClose} />
                <ConfirmButton
                  disabled={!data.min}
                  transitionState={confirmButtonState}
                  type="submit"
                  data-test-id="submit"
                >
                  <FormattedMessage
                    id="DM/Ha1"
                    defaultMessage="Add"
                    description="add postal code range, button"
                  />
                </ConfirmButton>
              </DashboardModal.Actions>
            </DashboardModal.Grid>
          )}
        </Form>
      </DashboardModal.Content>
    </DashboardModal>
  );
};

ShippingZonePostalCodeRangeDialog.displayName = "ShippingZonePostalCodeRangeDialog";
export default ShippingZonePostalCodeRangeDialog;
